import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ErrorWidget, LoaderWidget, useI18n } from '@jarvis/react-portal-addons';
import { Avatar } from '@veneer/core';
import SideCardTemplate from '../SideCards/SideCardTemplate';
import { useAdminUsersApiCall } from '../../../hooks/useAdminUsersApiCall';
import {
  AddonsWidgetContainer,
  AvatarContainer,
  Container,
  Email,
  IconEnvelope,
  IconPhone,
  InfoContainer,
  InfoItem,
  PhoneNumber,
  UserName,
} from './styles';

export const AdminsWidget = ({ title }) => {
  const { t } = useI18n();

  const adminUsers = useAdminUsersApiCall();

  const handleRetry = useCallback(() => {
    adminUsers.forceFetch();
  }, [adminUsers]);

  // This method was copied from the react-common-layout-mfe to maintain consistency
  const getInitialsFromNames = (firstName, secondName) => {
    const firstLetter = firstName?.[0]?.toUpperCase() || '';
    const secondLetter = secondName?.[0]?.toUpperCase() || '';
    return firstLetter + secondLetter;
  };

  const Content = useMemo(() => {
    if (adminUsers?.pending) {
      return (
        <AddonsWidgetContainer data-testid="admins-widget-card-loading">
          <LoaderWidget />
        </AddonsWidgetContainer>
      );
    }

    if (adminUsers?.error) {
      return (
        <AddonsWidgetContainer data-testid="admins-widget-card-error">
          <ErrorWidget
            i18n={{
              retry: t('dashboard.error.retry'),
              message: t('dashboard.error.bodyCopy'),
            }}
            onRetry={handleRetry}
          />
        </AddonsWidgetContainer>
      );
    }

    return (
      <>
        {adminUsers?.data?.map((admin, index) => {
          const id = index + 1;
          return (
            <Container key={admin.email} data-testid={`admin-user-item-${id}`}>
              <AvatarContainer>
                <Avatar
                  size={48} label={getInitialsFromNames(admin.givenName, admin.familyName)}
                  data-testid={`admin-user-item-${id}-avatar`}
                />
              </AvatarContainer>
              <InfoContainer>
                <UserName className="label-regular" data-testid={`admin-user-item-${id}-name`}>{`${admin.givenName} ${admin.familyName}`}</UserName>
                <InfoItem>
                  <IconEnvelope size={17} />
                  <Email className="label-small" data-testid={`admin-user-item-${id}-email`}>{admin.email}</Email>
                </InfoItem>

                {admin.phoneNumber && (
                  <InfoItem>
                    <IconPhone size={16} />
                    <PhoneNumber
                      className="label-small"
                      data-testid={`admin-user-item-${id}-phone`}
                    >
                      {admin.phoneNumber}
                    </PhoneNumber>
                  </InfoItem>
                )}
              </InfoContainer>
            </Container>
          );
        })}
      </>
    );
  }, [adminUsers, handleRetry, t]);

  return (
    <SideCardTemplate
      title={title}
      content={Content}
      data-testid="admins-widget-card"
    />
  );
};

AdminsWidget.propTypes = {
  title: PropTypes.string.isRequired,
};

export default AdminsWidget;
